import { useEffect, useId, useRef, ReactNode, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useBackdropClickClose } from '../../hooks/useBackdropClickClose';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'wide';

export interface ModalProps {
  isOpen: boolean;
  manageFocus?: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  headerActions?: ReactNode;
  size?: ModalSize;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  hideCloseButton?: boolean;
  bodyClassName?: string;
  panelClassName?: string;
  bodyRef?: Ref<HTMLDivElement>;
  bodyKey?: string;
  children: ReactNode;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-4xl',
  wide: 'max-w-6xl',
};

/**
 * Modal z backdropem. Renderowany przez createPortal do document.body
 * (omija z-index w rodzicach). Escape + backdrop click zamykają (domyślnie).
 * Locks body scroll gdy otwarty.
 *
 * Do potwierdzeń (OK/Cancel) używaj istniejącego ConfirmDialog — nie duplikuj.
 * <Modal> jest dla bardziej rozbudowanych dialogów (formularze, detail views).
 */
export function Modal({
  isOpen,
  manageFocus = false,
  onClose,
  title,
  description,
  footer,
  headerActions,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  hideCloseButton = false,
  bodyClassName = '',
  panelClassName = '',
  bodyRef,
  bodyKey,
  children,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!isOpen) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (manageFocus) panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (document.querySelector('[role="listbox"]')) return;
      if (closeOnEscape && e.key === 'Escape') closeRef.current();
      if (manageFocus && e.key === 'Tab') {
        const panel = panelRef.current;
        if (!panel) return;
        const focusable = Array.from(panel.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]')).filter(node => node.getClientRects().length > 0);
        const first = focusable[0]; const last = focusable.at(-1);
        if (!first) { e.preventDefault(); panel.focus(); }
        else if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || document.activeElement === panel)) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (manageFocus && trigger?.isConnected) trigger.focus();
    };
  }, [isOpen, closeOnEscape, manageFocus]);

  const backdrop = useBackdropClickClose(onClose, closeOnBackdrop);

  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in"
      style={{ background: 'var(--modal-backdrop)' }}
      {...backdrop}
    >
      <div
        ref={panelRef}
        tabIndex={manageFocus ? -1 : undefined}
        className={`relative w-full ${SIZE_CLASSES[size]} bg-surface-raised border border-border rounded-card shadow-glass animate-scale-in flex flex-col max-h-[90vh] ${panelClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || !hideCloseButton) && (
          <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-border-subtle shrink-0">
            <div className="min-w-0 flex-1">
              {title && <h2 id={titleId} className="text-lg font-semibold text-text-primary leading-tight">{title}</h2>}
              {description && <p className="text-sm text-text-muted mt-1.5 leading-relaxed">{description}</p>}
            </div>
            {headerActions}
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors"
                aria-label="Zamknij"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        <div ref={bodyRef} key={bodyKey} className={`px-6 py-5 overflow-y-auto ${bodyClassName}`}>{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-border-subtle shrink-0">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
